import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/slices/authSlice";
import { useGetDetailedAnalyticsQuery } from "../redux/services/api";
import Icon from "../components/Icon";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

const Analytics = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  // Use RTK Query for analytics data
  const {
    data: analyticsResponse,
    isLoading: loading,
    refetch,
  } = useGetDetailedAnalyticsQuery();
  const analyticsData = analyticsResponse?.data || null;

  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [selectedPageType, setSelectedPageType] = useState("");
  const [customDateRange, setCustomDateRange] = useState({
    start: "",
    end: "",
  });

  // RTK Query handles data fetching automatically
  // Custom filters can be added by extending the API service if needed

  const handleDateRangeChange = () => {
    if (customDateRange.start && customDateRange.end) {
      setSelectedPeriod("custom");
    }
  };

  const formatNumber = (num) => {
    return num ? num.toLocaleString() : "0";
  };

  const getPeriodLabel = () => {
    if (customDateRange.start && customDateRange.end) {
      return `${customDateRange.start} to ${customDateRange.end}`;
    }
    return selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1);
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("analyticsDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-left">
          <button
            className="back-btn"
            onClick={() => navigate(getLocalizedPath("/dashboard"))}
          >
            <Icon name="arrowLeft" size="16px" />
            {t("analyticsDashboard.backToDashboard")}
          </button>
          <div className="header-title">
            <h1>{t("analyticsDashboard.analyticsDashboard")}</h1>
            <p>{t("analyticsDashboard.detailedVisitorAnalytics")}</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className={`save-btn ${loading ? "saving" : ""}`}
            onClick={() => refetch()}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                {t("analyticsDashboard.refreshing")}
              </>
            ) : (
              <>
                <Icon name="refresh" className="btn-icon" size="16px" />
                {t("analyticsDashboard.refresh")}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="analytics-filters">
        <div className="filter-group">
          <label>{t("analyticsDashboard.period")}:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="today">{t("analyticsDashboard.today")}</option>
            <option value="week">{t("analyticsDashboard.thisWeek")}</option>
            <option value="month">{t("analyticsDashboard.thisMonth")}</option>
            <option value="year">{t("analyticsDashboard.thisYear")}</option>
            <option value="custom">{t("analyticsDashboard.custom")}</option>
          </select>
        </div>

        {selectedPeriod === "custom" && (
          <div className="filter-group">
            <label>{t("analyticsDashboard.from")}:</label>
            <input
              type="date"
              value={customDateRange.start}
              onChange={(e) =>
                setCustomDateRange((prev) => ({
                  ...prev,
                  start: e.target.value,
                }))
              }
            />
            <label>{t("analyticsDashboard.to")}:</label>
            <input
              type="date"
              value={customDateRange.end}
              onChange={(e) =>
                setCustomDateRange((prev) => ({
                  ...prev,
                  end: e.target.value,
                }))
              }
            />
          </div>
        )}

        <div className="filter-group">
          <label>{t("analyticsDashboard.page")}:</label>
          <select
            value={selectedPageType}
            onChange={(e) => setSelectedPageType(e.target.value)}
          >
            <option value="">{t("analyticsDashboard.page")}</option>
            <option value="home">Homepage</option>
            <option value="service">Services</option>
            <option value="offer">Offers</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      {analyticsData && (
        <div className="analytics-overview">
          <div className="overview-card">
            <div className="overview-icon">
              <Icon name="users" size="24px" />
            </div>
            <div className="overview-content">
              <h3>{formatNumber(analyticsData.overview?.total_visitors)}</h3>
              <p>{t("analyticsDashboard.totalVisitors")}</p>
              <span className="overview-period">{getPeriodLabel()}</span>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">
              <Icon name="eye" size="24px" />
            </div>
            <div className="overview-content">
              <h3>{formatNumber(analyticsData.overview?.total_visits)}</h3>
              <p>{t("analyticsDashboard.totalPageViews")}</p>
              <span className="overview-period">{getPeriodLabel()}</span>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">
              <Icon name="document" size="24px" />
            </div>
            <div className="overview-content">
              <h3>{formatNumber(analyticsData.overview?.unique_pages)}</h3>
              <p>{t("analyticsDashboard.uniquePages")}</p>
              <span className="overview-period">{getPeriodLabel()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Charts and Data Grids */}
      <div className="analytics-content">
        {/* Page Visits */}
        <div className="analytics-section">
          <h2>{t("analyticsDashboard.mostVisitedPages")}</h2>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{t("analyticsDashboard.page")}</th>
                  <th>{t("analyticsDashboard.type")}</th>
                  <th>{t("analyticsDashboard.visits")}</th>
                  <th>{t("analyticsDashboard.percentage")}</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData?.page_visits?.map((page, index) => (
                  <tr key={index}>
                    <td>{page.page_title || page.page_path}</td>
                    <td>
                      <span className={`page-type-badge ${page.page_type}`}>
                        {page.page_type}
                      </span>
                    </td>
                    <td>{formatNumber(page.visit_count)}</td>
                    <td>
                      {analyticsData.overview?.total_visits
                        ? (
                            (page.visit_count /
                              analyticsData.overview.total_visits) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </td>
                  </tr>
                )) || []}
              </tbody>
            </table>
          </div>
        </div>

        {/* Service Visits */}
        <div className="analytics-section">
          <h2>{t("analyticsDashboard.serviceVisits")}</h2>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{t("analyticsDashboard.service")}</th>
                  <th>{t("analyticsDashboard.visits")}</th>
                  <th>{t("analyticsDashboard.percentage")}</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData?.service_visits?.map((service, index) => (
                  <tr key={index}>
                    <td>{service.title}</td>
                    <td>{formatNumber(service.visits)}</td>
                    <td>
                      {(() => {
                        const totalServiceVisits =
                          analyticsData.service_visits?.reduce(
                            (sum, s) => sum + s.visits,
                            0
                          ) || 1;
                        return (
                          (service.visits / totalServiceVisits) *
                          100
                        ).toFixed(1);
                      })()}
                      %
                    </td>
                  </tr>
                )) || []}
                {(!analyticsData?.service_visits ||
                  analyticsData.service_visits.length === 0) && (
                  <tr>
                    <td colSpan="3" className="no-data">
                      {t("analyticsDashboard.noServiceVisitsFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Offer Visits */}
        <div className="analytics-section">
          <h2>{t("analyticsDashboard.offerVisits")}</h2>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{t("analyticsDashboard.offer")}</th>
                  <th>{t("analyticsDashboard.visits")}</th>
                  <th>{t("analyticsDashboard.percentage")}</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData?.offer_visits?.map((offer, index) => (
                  <tr key={index}>
                    <td>{offer.title}</td>
                    <td>{formatNumber(offer.visits)}</td>
                    <td>
                      {(() => {
                        const totalOfferVisits =
                          analyticsData.offer_visits?.reduce(
                            (sum, o) => sum + o.visits,
                            0
                          ) || 1;
                        return (
                          (offer.visits / totalOfferVisits) *
                          100
                        ).toFixed(1);
                      })()}
                      %
                    </td>
                  </tr>
                )) || []}
                {(!analyticsData?.offer_visits ||
                  analyticsData.offer_visits.length === 0) && (
                  <tr>
                    <td colSpan="3" className="no-data">
                      {t("analyticsDashboard.noOfferVisitsFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Referrers */}
        {analyticsData?.top_referrers &&
          analyticsData.top_referrers.length > 0 && (
            <div className="analytics-section">
              <h2>Top Referrers</h2>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Referrer</th>
                      <th>Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.top_referrers.map((referrer, index) => (
                      <tr key={index}>
                        <td>
                          <a
                            href={referrer.referer}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {referrer.referer}
                          </a>
                        </td>
                        <td>{formatNumber(referrer.visits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default Analytics;
